'use client'

import React, { useEffect, FC, ReactElement, useRef } from 'react'
import { safeDecodeCollectionSlug } from '@/utils/collection-object'
import { createChart, ColorType, LineType } from 'lightweight-charts'
import type { AutoscaleInfoProvider, IChartApi, LineData, Time } from 'lightweight-charts'
import { useTheme } from 'next-themes'
import { NETWORK } from '@/utils/constants'
import { formatSatsPer } from '@/utils/format-sats'
import isFungible from '@/utils/is-fungible'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import classNames from 'classnames'
import CircleLoading from '@/components/circle-loading'
import useWindowSize from '@/utils/hooks/useWindowSize'
import {
	isFairPriceSeries,
	shouldUseLegacyCollectionChart,
	useCollectionVWAPRange,
	VWAPInterval
} from '../../../utils/hooks/useCollectionVWAP'
import { useMark } from '@/lib/quotes/hooks'
import useCollectionMeta from '@/utils/hooks/useCollectionMeta'
import useTokenPrices from '@/utils/hooks/useTokenPrices'
import useEscrows from '@/utils/hooks/useEscrows'
import useSoldEscrows from '@/utils/hooks/useSoldEscrows'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { getSatoshiPriceUSD } from '@/utils/hooks/useSatoshiPriceUSD'
import { formatUsdPerBillion } from '@/utils/format-usd'
import { Minus, Plus, RotateCcw } from 'lucide-react'
import PercentChange from '@/components/percent-change'
import CollectionIcon from '@/components/collection-icon'
import VerifiedBadge from '@/components/verified-badge'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import { isCollectionObjectSlug } from '@/utils/collection-object'

// Using daily VWAP buckets now; prior intraday grouping logic removed

// const bucketTabs = ['5m', '1h', '4h', '1D'] // using daily buckets only
const scaleTabs = ['Linear', 'Log', 'Percent']
type PriceDomain = 'Typical' | 'Full'

const rangeOptions: { label: string; days: number | 'ytd' | null }[] = [
	{ label: '1D', days: 1 },
	{ label: '1W', days: 7 },
	{ label: '1M', days: 30 },
	{ label: '3M', days: 90 },
	{ label: 'YTD', days: 'ytd' },
	{ label: '1Y', days: 365 },
	{ label: 'ALL', days: null }
]

// Bucket size per range. Hourly for the short windows, four-hour for the
// month -- a daily line over thirty days is a staircase -- and daily beyond.
const RANGE_INTERVAL: Record<string, VWAPInterval> = {
	'1D': '1h',
	'1W': '1h',
	'1M': '4h',
	'3M': '1d',
	YTD: '1d',
	'1Y': '1d',
	ALL: '1d'
}

const SECONDS_PER_DAY = 24 * 60 * 60

/** A dollar figure at the precision its size needs: $1,533.46, $12,400, $0.0412. */
const formatUsd = (value: number) => {
	if (!Number.isFinite(value)) return ''
	const abs = Math.abs(value)
	const digits = abs >= 10_000 ? 0 : abs >= 1 ? 2 : 4
	return `$${value.toLocaleString('en-US', {
		minimumFractionDigits: digits,
		maximumFractionDigits: digits
	})}`
}

// Cap "ALL" at what the server will serve in one contiguous request set.
// 6 × 5y = 30y of daily is plenty; no real collection comes close.
const ALL_MAX_DAYS = 6 * 5 * 365

const resolveRangeDays = (d: number | 'ytd' | null): number | null => {
	if (d === 'ytd') {
		const now = new Date()
		const soy = new Date(now.getFullYear(), 0, 1)
		return Math.ceil((now.getTime() - soy.getTime()) / (24 * 60 * 60 * 1000))
	}
	return d
}

const buildRangeQuery = (selectedRange: string) => {
	const nowMs = Date.now()
	const interval = RANGE_INTERVAL[selectedRange] ?? '1d'
	const bucketSeconds =
		interval === '1h' ? 60 * 60 : interval === '4h' ? 4 * 60 * 60 : SECONDS_PER_DAY
	const now = Math.floor(nowMs / 1000)
	const end = Math.floor(now / bucketSeconds) * bucketSeconds
	const opt = rangeOptions.find((r) => r.label === selectedRange)
	const resolvedDays = opt
		? opt.days === 'ytd'
			? Math.ceil((nowMs - new Date(new Date(nowMs).getFullYear(), 0, 1).getTime()) / 86400000)
			: opt.days ?? ALL_MAX_DAYS
		: 7
	const start =
		opt?.days === 'ytd'
			? Math.floor(Date.UTC(new Date(nowMs).getUTCFullYear(), 0, 1) / 1000)
			: end - (resolvedDays || 7) * SECONDS_PER_DAY
	return {
		interval,
		start,
		end
	}
}

const CollectionChart: FC<{
	slug: string
	width: number
	height: number
	minimal?: boolean
	/** Render an internal price + range-% header above the canvas. Useful on
	 *  the main collection page; disable when the parent (e.g. modal body)
	 *  renders its own price block. */
	showPrice?: boolean
	/** Keep the date/price axes and quiet grid in a compact embed whose parent
	 * renders the price header. */
	showAxisLabels?: boolean
	/** Hide the duplicate collection identity when the page header already
	 *  supplies it, freeing more vertical room for the plot. */
	showIdentity?: boolean
	/** Optional controls supplied by the parent workspace (panel toggles, etc.). */
	headerActions?: React.ReactNode
	/** Optional collection metadata used to render the icon + name row above
	 *  the price in the minimal header. */
	collection?: any
	onHoverPrice?: (label: string | null) => void
	onRangeChange?: (label: string, percentChange: number) => void
}> = (props): ReactElement => {
	const {
		slug,
		width,
		height,
		minimal = false,
		showPrice = false,
		showAxisLabels = false,
		showIdentity = true,
		headerActions,
		collection,
		onHoverPrice,
		onRangeChange
	} = props
	const { isMobile } = useWindowSize()

	// Observe the chart wrapper so the lightweight-charts canvas always matches
	// the actual rendered container width (beats relying on a possibly-stale
	// `width` prop from an ancestor).
	const wrapperRef = useRef<HTMLDivElement>(null)
	const [measuredWidth, setMeasuredWidth] = React.useState(0)
	useEffect(() => {
		if (!wrapperRef.current) return
		const measure = () => {
			if (wrapperRef.current) setMeasuredWidth(wrapperRef.current.offsetWidth)
		}
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(wrapperRef.current)
		return () => observer.disconnect()
	}, [])
	const effectiveWidth = measuredWidth || width || 300
	// Collection APIs are case-sensitive for protocol slugs. Prefer the API's
	// canonical slug from metadata when the public route uses different casing.
	const dataSlug =
		typeof collection?.slug === 'string' && collection.slug.length > 0 ? collection.slug : slug
	// The series is the fair price for every collection with a cross-market
	// tape -- the terminal's line, the homepage's 7d source -- and a VWAP
	// only where no such tape exists.
	const vwapLabel = shouldUseLegacyCollectionChart(dataSlug)
		? 'OW VWAP'
		: isCollectionObjectSlug(dataSlug)
		? 'Collection VWAP'
		: 'Fair price'

	// Any render guards (invalid slug, load error, missing data) are evaluated
	// AFTER every hook below, so hook call order stays stable across renders
	// (React's rules-of-hooks requirement). Guard values here, branch at the
	// bottom of the component.
	const slugInvalid = !slug || typeof slug !== 'string'

	// In minimal mode we also render a price header (optional) and a tabs row
	// inside the wrapper; reserve that space out of the external height so the
	// whole block fits without overflowing.
	const MINIMAL_TABS_H = 52
	const MINIMAL_PRICE_H = showPrice ? (collection && showIdentity ? 136 : 88) : 0
	const chartHeight = minimal
		? Math.max((height || 200) - MINIMAL_TABS_H - MINIMAL_PRICE_H, 100)
		: height || 200

	// `isFungible('')` returns falsy so this is safe when slug is invalid; the
	// slugInvalid guard at the bottom of the component will short-circuit the
	// render before any consumer of `fungible` runs.
	const fungible = isFungible(slug || '')

	// const [selectedBucketTab, setSelectedBucketTab] = React.useState('1h')
	const [selectedScaleTab, setSelectedScaleTab] = React.useState(fungible ? 'Log' : 'Linear')
	const [priceDomain, setPriceDomain] = React.useState<PriceDomain>(fungible ? 'Typical' : 'Full')
	const ref = useRef<HTMLDivElement>(null)
	const chartApiRef = useRef<IChartApi | null>(null)
	const visibleRangeRef = useRef<{ from: number; to: number } | null>(null)
	const defaultVisibleRangeRef = useRef<{ from: number; to: number } | null>(null)
	const hasCustomViewRef = useRef(false)
	// A month is the opening view: it shows where the fair price has been
	// going, where a week of a glided line is mostly a slope.
	const defaultRange = '1M'
	const [selectedRange, setSelectedRange] = React.useState<string>(defaultRange)
	const [rangeQuery, setRangeQuery] = React.useState(() => buildRangeQuery(defaultRange))
	const handleSelectRange = React.useCallback((label: string) => {
		hasCustomViewRef.current = false
		visibleRangeRef.current = null
		defaultVisibleRangeRef.current = null
		setSelectedRange(label)
		setRangeQuery(buildRangeQuery(label))
	}, [])
	useEffect(() => {
		const refreshRange = () => setRangeQuery(buildRangeQuery(selectedRange))
		const timer = window.setInterval(refreshRange, 60_000)
		return () => window.clearInterval(timer)
	}, [selectedRange])

	// Map the currently selected range tab to (interval, window). The hook
	// splits the window into server-sized chunks and stitches them — every
	// chunk URL is independently cacheable at the CDN. Picking hourly for
	// ≤30d keeps the candle count well under 1k per request.
	const { interval, start, end } = rangeQuery
	const {
		data: vwapData,
		error,
		isLoading
	} = useCollectionVWAPRange(dataSlug || '', interval, start, end, {
		keepPreviousData: false
	})

	// Hover-only price; null when the cursor isn't over the chart so the
	// default display falls through to `externalPrice` (see below).
	const [hoveredValue, setHoveredValue] = React.useState<number | null>(null)
	// Hover-relative percent (delta from selected-range start to hovered point).
	// Kept in its own state so hover never mutates `rangePct` — that variable
	// drives `isDown`, which is a chart-effect dependency, so updating it on
	// hover would tear down and rebuild the chart on every move and the
	// displayed pct would stick on the at-rest baseline.
	const [hoveredPct, setHoveredPct] = React.useState<number | null>(null)
	const [hoveredTime, setHoveredTime] = React.useState<number | null>(null)
	const onHoverPriceRef = useRef(onHoverPrice)
	const onRangeChangeRef = useRef(onRangeChange)
	const lastRangeEmissionRef = useRef<{ label: string; value: number } | null>(null)

	useEffect(() => {
		onHoverPriceRef.current = onHoverPrice
	}, [onHoverPrice])

	useEffect(() => {
		onRangeChangeRef.current = onRangeChange
	}, [onRangeChange])

	const emitRangeChange = React.useCallback((label: string, value: number) => {
		const current = lastRangeEmissionRef.current
		if (current?.label === label && Math.abs(current.value - value) < 0.000001) return
		const next = { label, value }
		lastRangeEmissionRef.current = next
		onRangeChangeRef.current?.(label, value)
	}, [])

	// Match the header's pricing hierarchy exactly: live ask, recent sale, then
	// cached reference price. These hooks share the header's SWR cache.
	const { data: tokenPrices } = useTokenPrices()
	const { data: liveEscrows } = useEscrows(dataSlug)
	const { data: recentSoldEscrows } = useSoldEscrows(dataSlug)
	const { exchangeRate } = useExchangeRate()
	const fairSeries = isFairPriceSeries(dataSlug)
	// A fair-price line is drawn in dollars: 0.01814 on an axis is noise,
	// $1,533 is a price. The rate is taken once, when it first arrives, so a
	// tick of BTC never redraws the line; every plotted value is the sats
	// series times that one rate, and the 1W figure stays the site's.
	const usdChart = fairSeries && !fungible
	const [usdRate, setUsdRate] = React.useState<number | null>(null)
	React.useEffect(() => {
		if (usdRate === null && Number.isFinite(exchangeRate) && exchangeRate > 0) {
			setUsdRate(exchangeRate)
		}
	}, [exchangeRate, usdRate])
	// What one BTC of series value is drawn as. `null` until the rate lands.
	const plotScale: number | null = usdChart ? usdRate : 1
	// The week's change the rest of the site prints, so the 1W chip here
	// cannot disagree with the header or the homepage.
	const liveMark = useMark(fairSeries ? dataSlug : null)
	const { data: liveMeta } = useCollectionMeta(fairSeries ? dataSlug : '')
	const fairWeekChange = [
		liveMark?.change_week,
		liveMeta?.change_week_fair,
		(collection as any)?.change_week_fair
	].find((v: unknown): v is number => typeof v === 'number' && Number.isFinite(v))
	const externalPriceInfo = React.useMemo<{
		value: number | null
		label: 'Best ask' | 'Last sale' | 'Reference price' | 'OW floor'
	}>(() => {
		try {
			// A fair-price line is headlined by its own latest value, not by
			// the floor of one venue's listings; `null` here falls through to
			// the line's last point (see `displayValue`).
			if (fairSeries && !fungible) {
				return { value: null, label: 'OW floor' }
			}
			if (fungible && Array.isArray(liveEscrows)) {
				const asks = liveEscrows
					.filter((entry: any) => entry?.satoshi_price && entry?.amount)
					.map((entry: any) => entry.satoshi_price / parseFloat(entry.amount || '1'))
					.filter((value: number) => Number.isFinite(value) && value > 0)
				if (asks.length > 0) return { value: Math.min(...asks), label: 'Best ask' }
			}
			if (fungible && Array.isArray(recentSoldEscrows)) {
				const latest = [...recentSoldEscrows]
					.filter((entry: any) => entry?.satoshi_price && entry?.amount)
					.sort(
						(a: any, b: any) =>
							new Date(b.bought_at || b.sold_at || 0).getTime() -
							new Date(a.bought_at || a.sold_at || 0).getTime()
					)[0]
				if (latest) {
					const value = latest.satoshi_price / parseFloat(latest.amount || '1')
					if (Number.isFinite(value) && value > 0) return { value, label: 'Last sale' }
				}
			}
			const entry = (tokenPrices || []).find(
				(e: any) => e.slug === safeDecodeCollectionSlug(dataSlug)
			)
			const p = entry?.floor_price_per ?? (collection as any)?.floor_price_per
			const n = typeof p === 'string' ? parseFloat(p) : typeof p === 'number' ? p : NaN
			return {
				value: Number.isFinite(n) && n > 0 ? n : null,
				label: fungible ? 'Reference price' : 'OW floor'
			}
		} catch {
			return { value: null, label: fungible ? 'Reference price' : 'OW floor' }
		}
	}, [collection, dataSlug, fairSeries, fungible, liveEscrows, recentSoldEscrows, tokenPrices])
	const externalPrice = externalPriceInfo.value

	// Shared data pipeline for the truthful VWAP step-area series.
	const lineData = React.useMemo(() => {
		if (!Array.isArray(vwapData) || plotScale === null) {
			return [] as { time: number; value: number }[]
		}
		return (vwapData as any[])
			.flatMap((d) =>
				d && typeof d.time === 'number' && typeof d.vwap === 'number'
					? [{ time: d.time as number, value: fungible ? d.vwap * 1e8 : d.vwap * plotScale }]
					: []
			)
			.sort((a, b) => a.time - b.time)
	}, [vwapData, fungible, plotScale])

	// Thin markets can contain real single-sale spikes that make the useful
	// price band unreadably small. Typical view computes a robust Tukey fence
	// in log-price space while preserving every raw point. Anything outside the
	// visible domain is counted and can be restored with one click via Full.
	const typicalDomain = React.useMemo(() => {
		const values = lineData
			.map((point) => point.value)
			.filter((value) => Number.isFinite(value) && value > 0)
			.sort((a, b) => a - b)
		if (!fungible || values.length < 8) return null

		const logs = values.map(Math.log)
		const quantile = (series: number[], percentile: number) => {
			const position = (series.length - 1) * percentile
			const lower = Math.floor(position)
			const upper = Math.ceil(position)
			if (lower === upper) return series[lower]
			return series[lower] + (series[upper] - series[lower]) * (position - lower)
		}
		const q1 = quantile(logs, 0.25)
		const q3 = quantile(logs, 0.75)
		const spread = Math.max(q3 - q1, 0.02)
		const minValue = Math.exp(q1 - spread * 1.5)
		const maxValue = Math.exp(q3 + spread * 1.5)
		const outlierCount = values.filter((value) => value < minValue || value > maxValue).length

		return outlierCount > 0 ? { minValue, maxValue, outlierCount } : null
	}, [fungible, lineData])

	const visibleInfo = React.useMemo(() => {
		if (lineData.length === 0) return { startIdx: 0, lastIdx: 0, rangePct: 0 }
		const rangeOpt = rangeOptions.find((r) => r.label === selectedRange)
		const resolvedDays = resolveRangeDays(rangeOpt?.days ?? null)
		const lastIdx = lineData.length - 1
		const lastTime = lineData[lastIdx].time
		let startIdx = 0
		if (resolvedDays) {
			const fromTime = lastTime - resolvedDays * 24 * 60 * 60
			const found = lineData.findIndex((p) => p.time >= fromTime)
			startIdx = found < 0 ? 0 : found
		}
		const startPt = lineData[startIdx]
		const endPt = lineData[lastIdx]
		const pct =
			startPt?.value && endPt?.value ? ((endPt.value - startPt.value) / startPt.value) * 100 : 0
		// The 1W figure is the site's one 7d number when the API has it.
		const weekPct = selectedRange === '1W' && fairWeekChange !== undefined ? fairWeekChange : null
		return {
			startIdx,
			lastIdx,
			rangePct: weekPct ?? (Number.isFinite(pct) ? pct : 0)
		}
	}, [lineData, selectedRange, fairWeekChange])
	const rangePct = visibleInfo.rangePct
	// The at-rest figure for the selected range. At 1W it is the site's one
	// 7d number whenever the API has it; every path that reports a
	// percentage while nothing is hovered goes through here, so the chip
	// under the headline never disagrees with the header or the homepage.
	const atRestPct = React.useCallback(
		(computed: number) =>
			selectedRange === '1W' && fairWeekChange !== undefined
				? fairWeekChange
				: Number.isFinite(computed)
				? computed
				: 0,
		[selectedRange, fairWeekChange]
	)
	const lastPrice = React.useMemo(() => {
		const last = lineData[lineData.length - 1]
		return last ? { value: last.value, time: last.time } : { value: 0, time: 0 }
	}, [lineData])
	const changePct = React.useMemo(() => {
		if (lineData.length < 2) return 0
		const a = lineData[lineData.length - 2].value
		const b = lineData[lineData.length - 1].value
		return a > 0 ? ((b - a) / a) * 100 : 0
	}, [lineData])

	// Keep rangePct + onRangeChange in sync with the selected range.
	// Only notify the parent once we have real data so the header doesn't flash
	// a placeholder "0.00%" before the VWAP response arrives.
	useEffect(() => {
		if (lineData.length > 1) {
			emitRangeChange(selectedRange, visibleInfo.rangePct)
		}
	}, [selectedRange, visibleInfo.rangePct, lineData.length, emitRangeChange])

	// Red when the selected range closed lower than it opened; green otherwise.
	// Keep the exact brand tokens used for candles so the UI reads as one system.
	// In light mode, swap to darker shades so the line pops against white.
	const { resolvedTheme } = useTheme()
	const isLight = resolvedTheme === 'light'
	const upColor = isLight ? '#039855' : '#6CE9A6'
	const downColor = isLight ? '#B42318' : '#F97066'
	const upRgb = isLight ? '3, 152, 85' : '108, 233, 166'
	const downRgb = isLight ? '180, 35, 24' : '249, 112, 102'
	const isDown = rangePct < 0
	const lineAccent = isDown ? downColor : upColor
	const gridColor = isLight ? 'rgba(15, 23, 42, .07)' : 'rgba(255, 255, 255, .08)'
	const showAxes = showAxisLabels || !minimal || showPrice

	const zoomChart = React.useCallback((factor: number) => {
		const timeScale = chartApiRef.current?.timeScale()
		const range = timeScale?.getVisibleLogicalRange()
		if (!timeScale || !range) return
		const center = (range.from + range.to) / 2
		const halfSpan = ((range.to - range.from) * factor) / 2
		const next = { from: center - halfSpan, to: center + halfSpan }
		hasCustomViewRef.current = true
		visibleRangeRef.current = next
		timeScale.setVisibleLogicalRange(next)
	}, [])

	const resetChartView = React.useCallback(() => {
		const chart = chartApiRef.current
		if (!chart) return
		hasCustomViewRef.current = false
		const defaultRange = defaultVisibleRangeRef.current
		if (defaultRange) {
			visibleRangeRef.current = defaultRange
			chart.timeScale().setVisibleLogicalRange(defaultRange)
		} else {
			chart.timeScale().fitContent()
		}
		chart.priceScale('right').applyOptions({ autoScale: true })
	}, [])
	const togglePriceDomain = React.useCallback(() => {
		setPriceDomain((current) => (current === 'Typical' ? 'Full' : 'Typical'))
	}, [])

	useEffect(() => {
		if (effectiveWidth > 0) chartApiRef.current?.applyOptions({ width: effectiveWidth })
	}, [effectiveWidth])

	useEffect(() => {
		if (!vwapData || !Array.isArray(vwapData) || plotScale === null) return
		const preservedRange = hasCustomViewRef.current ? visibleRangeRef.current : null

		// The series is { time: seconds, vwap: BTC }. Fungibles plot sats per
		// unit; a fair-price line plots dollars; anything else plots BTC.
		const lineData: Array<LineData<Time>> = vwapData
			.flatMap((d) =>
				d && typeof d.time === 'number' && typeof d.vwap === 'number'
					? [{ time: d.time as Time, value: fungible ? d.vwap * 1e8 : d.vwap * plotScale }]
					: []
			)
			.sort((a, b) => Number(a.time as any) - Number(b.time as any))

		const handleResize = () => {
			if (ref.current) {
				// @ts-ignore
				chart.applyOptions({ width: ref.current.clientWidth })
			}
		}

		if (!ref.current) {
			return
		}
		const initialWidth = ref.current.clientWidth || 300

		const chart = createChart(ref.current, {
			layout: {
				background: {
					type: ColorType.Solid,
					color: minimal ? 'rgba(0,0,0,0)' : isLight ? '#FFFFFF' : 'black'
				},
				textColor: isLight ? '#0F172A' : 'white',
				attributionLogo: !minimal
			},
			grid: {
				vertLines: {
					visible: showAxes,
					color: gridColor
				},
				horzLines: {
					visible: showAxes,
					color: gridColor
				}
			},
			rightPriceScale: {
				mode: scaleTabs.findIndex((e) => e === selectedScaleTab),
				autoScale: true,
				scaleMargins: { top: 0.12, bottom: 0.16 },
				borderVisible: false,
				visible: showAxes
			},
			leftPriceScale: {
				visible: false,
				borderVisible: false
			},
			localization: {
				priceFormatter: (p: number) => {
					if (usdChart) return formatUsd(p)
					const { sats } = formatSatsPer(p)
					const coin = formatSatoshis(p * 1e8)
					return fungible ? `${sats} sats` : `${coin} ${NETWORK}`
				}
			},
			width: initialWidth,
			height: chartHeight,
			timeScale: {
				rightOffset: minimal ? 0 : isMobile ? 4 : 10,
				barSpacing: isMobile ? 6 : 10,
				fixLeftEdge: true,
				fixRightEdge: minimal,
				lockVisibleTimeRangeOnResize: true,
				rightBarStaysOnScroll: true,
				borderVisible: false,
				visible: showAxes,
				timeVisible: true,
				secondsVisible: false
			},
			handleScroll: {
				mouseWheel: true,
				pressedMouseMove: true,
				horzTouchDrag: true,
				vertTouchDrag: true
			},
			handleScale: {
				axisPressedMouseMove: true,
				mouseWheel: true,
				pinch: true
			}
		})
		chartApiRef.current = chart

		// The endpoint supplies VWAP only, not true OHLC. A step-area series is
		// honest about carry-forward periods and avoids synthetic candlesticks.
		const series = chart.addAreaSeries({
			lineColor: lineAccent,
			topColor: isDown ? `rgba(${downRgb}, 0.18)` : `rgba(${upRgb}, 0.18)`,
			bottomColor: isDown ? `rgba(${downRgb}, 0.00)` : `rgba(${upRgb}, 0.00)`,
			lineWidth: 2,
			lineType: LineType.WithSteps,
			priceLineVisible: showAxes,
			priceLineColor: lineAccent,
			priceLineWidth: 1,
			crosshairMarkerVisible: true,
			crosshairMarkerRadius: 4,
			crosshairMarkerBorderColor: lineAccent,
			crosshairMarkerBackgroundColor: isLight ? '#FFFFFF' : '#0B0B0B',
			autoscaleInfoProvider: ((original) => {
				if (priceDomain === 'Full' || !typicalDomain) return original()
				return {
					priceRange: {
						minValue: typicalDomain.minValue,
						maxValue: typicalDomain.maxValue
					}
				}
			}) satisfies AutoscaleInfoProvider
		})
		series.setData(lineData)

		// Visible-range start drives hover-pct so the chip the user sees while
		// hovering is "(hovered − range start) / range start", consistent with
		// the resting baseline shown when the cursor leaves the canvas.
		let crosshairStartValue: number | null = null
		let crosshairBaselinePct = 0
		if (lineData.length > 0) {
			const rangeOpt = rangeOptions.find((r) => r.label === selectedRange)
			const resolvedDays = resolveRangeDays(rangeOpt?.days ?? null)
			const lastIdxLocal = lineData.length - 1
			const lastTimeLocal = Number(lineData[lastIdxLocal].time)
			let startIdxLocal = 0
			if (resolvedDays) {
				const fromTime = lastTimeLocal - resolvedDays * 24 * 60 * 60
				const found = lineData.findIndex((p) => Number(p.time) >= fromTime)
				startIdxLocal = found < 0 ? 0 : found
			}
			const sv = lineData[startIdxLocal]?.value
			const ev = lineData[lastIdxLocal]?.value
			if (typeof sv === 'number') crosshairStartValue = sv
			if (sv && ev && sv > 0) crosshairBaselinePct = ((ev - sv) / sv) * 100
			crosshairBaselinePct = atRestPct(crosshairBaselinePct)
		}

		chart.subscribeCrosshairMove((param) => {
			const point = param?.seriesData?.get?.(series) as LineData<Time> | undefined
			const hoveredValue = point?.value

			if (typeof hoveredValue === 'number') {
				setHoveredValue(hoveredValue)
				setHoveredTime(typeof point?.time === 'number' ? point.time : null)
				if (typeof crosshairStartValue === 'number' && crosshairStartValue > 0) {
					const hoverPct = ((hoveredValue - crosshairStartValue) / crosshairStartValue) * 100
					setHoveredPct(hoverPct)
					if (lineData.length > 1) {
						emitRangeChange(selectedRange, hoverPct)
					}
				}
				const { sats: hoverSats } = formatSatsPer(hoveredValue)
				const label = usdChart
					? formatUsd(hoveredValue)
					: fungible
					? `${hoverSats} sats`
					: `${formatSatoshis(hoveredValue * 1e8)} ${NETWORK}`
				onHoverPriceRef.current?.(label)
			} else {
				setHoveredValue(null)
				setHoveredPct(null)
				setHoveredTime(null)
				if (minimal && lineData.length > 1) {
					emitRangeChange(selectedRange, crosshairBaselinePct)
				}
				onHoverPriceRef.current?.(null)
			}
		})

		// Set the visible range. In minimal mode it's driven by the selected
		// range tab; otherwise fall back to the last ~120 daily points.
		const timeScale = chart.timeScale()
		if (minimal && lineData.length > 0) {
			const rangeOpt = rangeOptions.find((r) => r.label === selectedRange)
			const resolvedDays = resolveRangeDays(rangeOpt?.days ?? null)
			const lastIdx = lineData.length - 1
			const lastTime = Number(lineData[lastIdx].time)

			let startIdx = 0
			if (resolvedDays) {
				const fromTime = lastTime - resolvedDays * 24 * 60 * 60
				const found = lineData.findIndex((p) => Number(p.time) >= fromTime)
				startIdx = found < 0 ? 0 : found
			}

			// Explicitly stretch bars to fill the canvas — setVisibleLogicalRange
			// alone doesn't reliably recompute barSpacing in lightweight-charts
			// 4.1, so short ranges leave a gap on the right edge. A 1.05x
			// multiplier zooms content slightly beyond the canvas so the line
			// always reaches both edges even with subpixel rounding.
			const visibleBars = lastIdx - startIdx + 1
			if (visibleBars > 0 && initialWidth > 0) {
				timeScale.applyOptions({ barSpacing: (initialWidth / visibleBars) * 1.05 })
			}
			timeScale.setVisibleLogicalRange({
				from: startIdx - 0.5,
				to: lastIdx + 0.5
			})

			const startPoint = lineData[startIdx]
			const endPoint = lineData[lastIdx]
			const computedRangePct =
				startPoint?.value && endPoint?.value
					? ((endPoint.value - startPoint.value) / startPoint.value) * 100
					: 0
			const finalPct = atRestPct(computedRangePct)
			if (lineData.length > 1) {
				emitRangeChange(selectedRange, finalPct)
			}
		} else {
			const numberOfPoints = 120
			if (lineData.length > numberOfPoints) {
				const lastDataIndex = lineData.length - 1
				const firstVisibleIndex = Math.max(0, lastDataIndex - numberOfPoints)
				timeScale.setVisibleRange({
					from: lineData[firstVisibleIndex].time,
					to: lineData[lastDataIndex].time
				})
			} else {
				timeScale.fitContent()
			}
		}

		const defaultVisibleRange = timeScale.getVisibleLogicalRange()
		if (defaultVisibleRange) {
			defaultVisibleRangeRef.current = { ...defaultVisibleRange }
			if (preservedRange) {
				visibleRangeRef.current = preservedRange
				timeScale.setVisibleLogicalRange(preservedRange)
			} else {
				visibleRangeRef.current = { ...defaultVisibleRange }
			}
		}
		const handleVisibleRangeChange = (range: { from: number; to: number } | null) => {
			if (range) visibleRangeRef.current = { ...range }
		}
		timeScale.subscribeVisibleLogicalRangeChange(handleVisibleRangeChange)

		window.addEventListener('resize', handleResize)

		// Belt-and-suspenders reset: subscribeCrosshairMove doesn't always fire
		// with empty seriesData when the cursor exits the chart on touch /
		// pointer-cancel paths, so the hover chip can stick on the last hovered
		// value. A native pointerleave on the container guarantees we revert
		// to the at-rest baseline (rangePct / changePct).
		const chartHost = ref.current
		const markCustomView = () => {
			hasCustomViewRef.current = true
		}
		const handleLeave = () => {
			setHoveredValue(null)
			setHoveredPct(null)
			setHoveredTime(null)
			if (minimal && lineData.length > 1) {
				emitRangeChange(selectedRange, crosshairBaselinePct)
			}
			onHoverPriceRef.current?.(null)
		}
		chartHost?.addEventListener('pointerleave', handleLeave)
		chartHost?.addEventListener('mouseleave', handleLeave)
		chartHost?.addEventListener('pointerdown', markCustomView)
		chartHost?.addEventListener('touchstart', markCustomView)
		chartHost?.addEventListener('wheel', markCustomView)

		return () => {
			window.removeEventListener('resize', handleResize)
			timeScale.unsubscribeVisibleLogicalRangeChange(handleVisibleRangeChange)
			chartHost?.removeEventListener('pointerleave', handleLeave)
			chartHost?.removeEventListener('mouseleave', handleLeave)
			chartHost?.removeEventListener('pointerdown', markCustomView)
			chartHost?.removeEventListener('touchstart', markCustomView)
			chartHost?.removeEventListener('wheel', markCustomView)
			if (chartApiRef.current === chart) chartApiRef.current = null
			chart.remove()
		}
	}, [
		selectedScaleTab,
		vwapData,
		fungible,
		plotScale,
		usdChart,
		height,
		isMobile,
		minimal,
		selectedRange,
		isDown,
		isLight,
		gridColor,
		chartHeight,
		upColor,
		downColor,
		lineAccent,
		downRgb,
		upRgb,
		emitRangeChange,
		showAxes,
		priceDomain,
		typicalDomain
	])

	const displayLastPrice = hoveredValue ?? lastPrice.value
	const { sats: displayLastPriceSats } = formatSatsPer(displayLastPrice)
	const renderChartState = (message?: string, isErrorState = false) => (
		<div
			ref={wrapperRef}
			className="relative flex size-full min-h-[220px] items-center justify-center"
		>
			{headerActions && <div className="absolute right-3 top-3 z-10">{headerActions}</div>}
			<div className="flex flex-col items-center justify-center gap-3 px-5 text-center">
				<CircleLoading size={36} />
				{message && (
					<p className={isErrorState ? 'text-sm text-red-500' : 'text-sm text-muted-foreground'}>
						{message}
					</p>
				)}
			</div>
		</div>
	)

	// const renderBucketTab = React.useCallback(
	// 	(e) => {
	// 		return (
	// 			<div
	// 				className={classNames(
	// 					'w-[60px] h-[28px] text-sm flex items-center justify-center font-semibold bg-chrome-muted cursor-pointer rounded-lg',
	// 					{ ['bg-success text-success-foreground']: selectedBucketTab === e }
	// 				)}
	// 				onClick={() => setSelectedBucketTab(e)}
	// 				key={e}
	// 			>
	// 				{e}
	// 			</div>
	// 		)
	// 	},
	// 	[selectedBucketTab]
	// )

	// Render guards — moved here so every hook above runs unconditionally on
	// every render (rules-of-hooks). Order matters only for message priority:
	// invalid slug wins over load error, which wins over the loading spinner.
	if (slugInvalid) {
		return renderChartState('Error: Invalid chart data', true)
	}

	if (error) {
		return renderChartState('Error: Unable to load chart data', true)
	}

	if (!vwapData) {
		return renderChartState('Loading chart data…')
	}

	if (minimal) {
		const hasChartData = Array.isArray(vwapData) && vwapData.length > 0
		const showLoader = isLoading && !hasChartData
		const values = lineData.map((point) => point.value).filter(Number.isFinite)
		const high = values.length ? Math.max(...values) : 0
		const low = values.length ? Math.min(...values) : 0
		const formatSummaryPrice = (value: number) =>
			usdChart
				? formatUsd(value)
				: fungible
				? `${formatSatsPer(value).sats} sats`
				: `${formatSatoshis(value * 1e8)} ${NETWORK}`
		const typicalSummary =
			priceDomain === 'Typical' && typicalDomain
				? ` Typical view has ${typicalDomain.outlierCount} price spike${
						typicalDomain.outlierCount === 1 ? '' : 's'
				  } outside the visible domain.`
				: ''
		const chartSummary = `${collection?.name || slug} ${vwapLabel} ${selectedRange} chart. ${
			lineData.length
				? `Low ${formatSummaryPrice(low)}, high ${formatSummaryPrice(high)}, change ${
						rangePct >= 0 ? 'plus ' : 'minus '
				  }${Math.abs(rangePct).toFixed(2)} percent.${typicalSummary}`
				: 'No chart data available.'
		}`
		// 1Y / ALL ranges pull years of daily buckets — show an explainer alongside
		// the spinner while the (uncached) chunks are still streaming in so users
		// don't stare at an empty canvas wondering whether the chart silently failed.
		const showLongRangeMessage = showLoader && (selectedRange === '1Y' || selectedRange === 'ALL')
		return (
			<div ref={wrapperRef} className="relative flex w-full flex-col overflow-hidden">
				{showPrice && (
					<div className="px-4 pb-3 pt-3 sm:px-5">
						{collection && showIdentity && (
							<div className="mb-3 flex items-center gap-3">
								<CollectionIcon
									className="size-10 shrink-0 overflow-hidden rounded-full"
									collection={collection}
								/>
								<h2 className="flex items-center gap-1 truncate text-base font-semibold text-foreground">
									<span className="truncate">{collection?.name}</span>
									<VerifiedBadge slug={slug} />
								</h2>
							</div>
						)}
						{(() => {
							// Hover shows the plotted VWAP; at rest show the canonical
							// market price source shared with the page header.
							const displayValue = hoveredValue ?? externalPrice ?? lastPrice.value
							const { sats: displaySats } = formatSatsPer(displayValue)
							// The on-chart value in sats: a fungible plots sats per
							// unit, a fair-price line plots dollars at the chart's
							// rate, anything else plots BTC.
							const valueInSats = fungible
								? displayValue
								: usdChart
								? usdRate
									? (displayValue / usdRate) * 1e8
									: 0
								: displayValue * 1e8
							const isNat = slug?.toUpperCase() === 'TAP-DMT-NAT'
							// Under a dollar headline the BTC figure; under a BTC or
							// sats headline the dollar figure.
							const usdLabel = usdChart
								? valueInSats > 0
									? `${formatSatoshis(valueInSats)} ${NETWORK}`
									: null
								: valueInSats > 0
								? isNat
									? formatUsdPerBillion(valueInSats * 1e-8 * exchangeRate)
									: `$${getSatoshiPriceUSD(valueInSats, exchangeRate)}`
								: null
							const valueLabel =
								hoveredValue !== null
									? vwapLabel
									: externalPrice !== null
									? externalPriceInfo.label
									: 'Latest fair price'
							const hoverDate = hoveredTime
								? new Intl.DateTimeFormat('en-US', {
										month: 'short',
										day: 'numeric',
										year: 'numeric',
										timeZone: 'UTC'
								  }).format(new Date(hoveredTime * 1000))
								: null
							return (
								<div className="flex items-start justify-between gap-4">
									<div className="min-w-0">
										<div className="mb-1 text-[10px] font-semibold text-muted-foreground">
											{valueLabel}
										</div>
										<div className="text-[30px] font-semibold leading-none tracking-[-0.02em] text-foreground sm:text-[32px]">
											{usdChart
												? formatUsd(displayValue)
												: fungible
												? displaySats
												: formatSatoshis(displayValue * 1e8)}
											{!usdChart && (
												<span className="ml-2 text-[16px] font-medium text-foreground/50 sm:text-[18px]">
													{fungible ? 'sats' : NETWORK}
												</span>
											)}
										</div>
										<div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm leading-none">
											{usdLabel && (
												<span className="font-medium text-foreground/65">{usdLabel}</span>
											)}
											<span className="inline-flex items-baseline gap-1">
												<PercentChange value={hoveredPct ?? rangePct} />
												<span className="text-muted-foreground">
													{vwapLabel} · {selectedRange}
												</span>
											</span>
											{hoverDate && <span className="text-muted-foreground">{hoverDate}</span>}
										</div>
									</div>
									{headerActions && <div className="shrink-0">{headerActions}</div>}
								</div>
							)
						})()}
					</div>
				)}
				<div className="relative w-full" style={{ height: chartHeight }}>
					{hasChartData && (
						<div ref={ref} className="h-full" role="img" aria-label={chartSummary} />
					)}
					{hasChartData && fungible && typicalDomain && (
						<button
							type="button"
							onClick={togglePriceDomain}
							className={`absolute right-3 top-2 z-10 inline-flex h-7 items-center rounded-full border bg-background/90 px-2.5 text-[10px] font-semibold shadow-sm backdrop-blur-sm focus-visible:ring-2 focus-visible:ring-ring ${
								priceDomain === 'Typical'
									? 'border-amber-500/30 text-amber-700 hover:bg-amber-500/10 dark:text-amber-300'
									: 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
							}`}
							aria-label={
								priceDomain === 'Typical'
									? `Show full price range, including ${typicalDomain.outlierCount} price spike${
											typicalDomain.outlierCount === 1 ? '' : 's'
									  }`
									: 'Focus on the typical price band'
							}
						>
							{priceDomain === 'Typical'
								? `${typicalDomain.outlierCount} spike${
										typicalDomain.outlierCount === 1 ? '' : 's'
								  } · Show full`
								: 'Full range · Show typical'}
						</button>
					)}
					{showLoader && (
						<div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
							<CircleLoading size={32} />
							{showLongRangeMessage && (
								<p className="text-[12px] text-muted-foreground">
									Loading data, this could take some time.
								</p>
							)}
						</div>
					)}
					{!hasChartData && !showLoader && (
						<div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
							Chart unavailable
						</div>
					)}
				</div>
				<div className="mt-auto flex min-h-[52px] items-center gap-2 border-t border-border/60 px-3 py-2">
					<div
						className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto scrollbar-hide"
						role="group"
						aria-label="Chart range"
					>
						{rangeOptions.map((opt) => (
							<button
								key={opt.label}
								type="button"
								onClick={() => handleSelectRange(opt.label)}
								aria-pressed={selectedRange === opt.label}
								className={classNames(
									'inline-flex h-8 shrink-0 items-center rounded-md px-2.5 text-xs font-semibold tracking-wide transition-colors focus-visible:ring-2 focus-visible:ring-ring',
									selectedRange === opt.label
										? 'bg-foreground text-background'
										: 'text-muted-foreground hover:bg-muted hover:text-foreground'
								)}
							>
								{opt.label}
							</button>
						))}
					</div>
					{showPrice && (
						<div className="ml-auto flex shrink-0 items-center gap-1">
							{fungible && typicalDomain && (
								<button
									type="button"
									onClick={togglePriceDomain}
									aria-pressed={priceDomain === 'Typical'}
									className={`inline-flex h-8 items-center rounded-md border px-2 text-[11px] font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
										priceDomain === 'Typical'
											? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
											: 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
									}`}
									title={
										priceDomain === 'Typical'
											? `Show all prices, including ${typicalDomain.outlierCount} spikes`
											: 'Focus on the typical price band'
									}
								>
									{priceDomain === 'Typical'
										? `Typical · ${typicalDomain.outlierCount}`
										: 'Full range'}
								</button>
							)}
							<div
								className="hidden items-center rounded-lg border border-border bg-muted/40 p-0.5 lg:flex"
								role="group"
								aria-label="Price scale"
							>
								{scaleTabs.slice(0, 2).map((scale) => (
									<button
										key={scale}
										type="button"
										onClick={() => setSelectedScaleTab(scale)}
										aria-pressed={selectedScaleTab === scale}
										className={`h-7 rounded-md px-2 text-[11px] font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
											selectedScaleTab === scale
												? 'bg-background text-foreground shadow-sm'
												: 'text-muted-foreground hover:text-foreground'
										}`}
									>
										{scale}
									</button>
								))}
							</div>
							<button
								type="button"
								onClick={() => zoomChart(1.4)}
								aria-label="Zoom chart out"
								title="Zoom out"
								className="hidden size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring sm:inline-flex"
							>
								<Minus className="size-4" aria-hidden="true" />
							</button>
							<button
								type="button"
								onClick={() => zoomChart(0.7)}
								aria-label="Zoom chart in"
								title="Zoom in"
								className="hidden size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring sm:inline-flex"
							>
								<Plus className="size-4" aria-hidden="true" />
							</button>
							<button
								type="button"
								onClick={resetChartView}
								aria-label="Reset chart view"
								title="Reset chart"
								className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
							>
								<RotateCcw className="size-4" aria-hidden="true" />
							</button>
						</div>
					)}
				</div>
			</div>
		)
	}

	return (
		<div className="relative overflow-hidden bg-gradient-to-b from-[#0B0B0B] to-[#0E0E10] shadow-lg">
			{/* Compact, Robinhood/WAGMI-style header */}
			<div className="absolute left-0 top-0 z-10 p-2 md:p-4 bg-background/50 backdrop-blur-sm border-b border-border w-full">
				<div className="flex items-end justify-between gap-2">
					<div className="flex flex-col">
						<p className="text-[10px] md:text-xs text-muted-foreground">Last Price</p>
						{/* // @ts-ignore */}
						<div className="flex items-center gap-2">
							<p className="text-lg md:text-2xl font-bold">
								{usdChart
									? formatUsd(displayLastPrice)
									: fungible
									? displayLastPriceSats
									: formatSatoshis(displayLastPrice * 1e8)}{' '}
								{usdChart ? '' : fungible ? 'sats' : NETWORK}
							</p>
							{(() => {
								const displayedPct = hoveredPct ?? changePct
								return (
									<span
										className={classNames(
											'text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded-full',
											displayedPct >= 0
												? 'bg-success/10 text-success'
												: 'bg-destructive/10 text-destructive'
										)}
									>
										{displayedPct >= 0 ? '+' : ''}
										{displayedPct.toFixed(2)}%
									</span>
								)
							})()}
						</div>
					</div>
					{/* Interval pills - compact on mobile (daily only for now) */}
					{/**
	        <div className="flex gap-1 md:gap-2">
	          {bucketTabs.map((e) => (
	            <div
	              key={e}
	              className={classNames(
	                'h-6 px-2 text-[10px] md:text-xs flex items-center justify-center font-semibold rounded-sm cursor-pointer transition-colors border border-border bg-transparent hover:bg-chrome-muted',
	                selectedBucketTab === e && 'border-success text-success bg-success/10'
	              )}
	              onClick={() => setSelectedBucketTab(e)}
	            >
	              {e}
	            </div>
	          ))}
	        </div>
	        **/}
				</div>
				{/* Secondary controls in one compact row */}
				<div className="flex gap-1 md:gap-2 mt-2">
					{scaleTabs.map((e) => (
						<div
							key={e}
							className={classNames(
								'h-6 px-2 text-[10px] md:text-xs flex items-center justify-center font-semibold rounded-sm cursor-pointer transition-colors border border-border bg-transparent hover:bg-chrome-muted',
								selectedScaleTab === e && 'border-success text-success bg-success/10'
							)}
							onClick={() => setSelectedScaleTab(e)}
							role="button"
							tabIndex={0}
							onKeyDown={onKeyboardClick(() => setSelectedScaleTab(e))}
						>
							{e}
						</div>
					))}
				</div>
			</div>
			{/* Chart canvas */}
			<div ref={ref} className="mt-[84px] md:mt-[110px]" />
		</div>
	)
}

export default CollectionChart
